import fs from "node:fs";
import path from "node:path";
import { spawn } from "node:child_process";
import { readManifest } from "../lib/config.js";

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 30);
}

function generateWranglerToml(slug: string, opts: { database: boolean; storage: boolean }): string {
  let toml = `name = "${slug}"
main = "../src/server/index.ts"
compatibility_date = "2025-04-01"
compatibility_flags = ["nodejs_compat"]
`;
  if (opts.database) {
    toml += `
[[d1_databases]]
binding = "DB"
database_name = "${slug}-db"
database_id = "local"
`;
  }
  if (opts.storage) {
    toml += `
[[r2_buckets]]
binding = "UPLOADS"
bucket_name = "${slug}-uploads"
`;
  }
  return toml;
}

function assertNodeVersion() {
  const [major] = process.versions.node.split(".").map(Number);
  if (major < 22) {
    console.error(
      `\n  Clawnify requires Node.js v22 or later.\n` +
      `  You are using v${process.versions.node}.\n\n` +
      `  Install a newer version: https://nodejs.org\n` +
      `  Or use a version manager: nvm use 22\n`
    );
    process.exit(1);
  }
}

export async function devCommand(dir?: string, options?: { port?: string }): Promise<void> {
  assertNodeVersion();
  const resolvedDir = dir ? path.resolve(dir) : process.cwd();
  const manifest = readManifest(resolvedDir);

  if (!manifest) {
    console.error("No clawnify.json found. Run `clawnify init` first.");
    process.exit(1);
  }

  if (!manifest.app) {
    console.error(
      "`clawnify dev` is for app projects only. For a website, run `pnpm dev` (astro dev) directly.",
    );
    process.exit(1);
  }

  const slug = slugify(manifest.name);
  const database = manifest.app.database !== false;
  const storage = manifest.app.storage === true;
  const apiPort = options?.port || "8787";

  // Generate wrangler config in .clawnify/
  const clawnifyDir = path.join(resolvedDir, ".clawnify");
  fs.mkdirSync(clawnifyDir, { recursive: true });
  const wranglerPath = path.join(clawnifyDir, "wrangler.toml");
  fs.writeFileSync(wranglerPath, generateWranglerToml(slug, { database, storage }));

  // Apply schema to local D1. Two paths, preferred order:
  //
  //   1. schema.ts + drizzle.config.ts (new, gold-standard):
  //      Run drizzle-kit generate to emit migrations into
  //      .clawnify/drizzle/, then apply unapplied .sql files via
  //      wrangler d1 execute --local. Track applied state in
  //      .clawnify/applied-migrations.json so reruns are idempotent.
  //      The user/agent only ever edits schema.ts.
  //
  //   2. schema.sql (legacy): apply directly. This is what older
  //      scaffolds shipped before drizzle support. Kept so existing
  //      apps don't regress.
  if (database) {
    const schemaTsPath = path.join(resolvedDir, "src/server/schema.ts");
    const drizzleConfigPath = path.join(resolvedDir, "drizzle.config.ts");
    if (fs.existsSync(schemaTsPath) && fs.existsSync(drizzleConfigPath)) {
      await syncDrizzleToLocalD1(resolvedDir, clawnifyDir, slug, wranglerPath);
    } else {
      const schemaCandidates = [
        path.join(resolvedDir, "schema.sql"),
        path.join(resolvedDir, "src/server/schema.sql"),
      ];
      const schemaPath = schemaCandidates.find((p) => fs.existsSync(p));
      if (schemaPath) {
        await runQuiet(resolvedDir, "npx", [
          "wrangler", "d1", "execute", `${slug}-db`,
          "--local", `--file=${schemaPath}`,
          `--config=${wranglerPath}`,
        ]);
      }
    }
  }

  console.log();
  console.log(`  ${manifest.name} dev server`);
  console.log();
  console.log(`  UI:   http://localhost:5173`);
  console.log(`  API:  http://localhost:${apiPort}`);
  console.log();

  // Spawn vite (frontend) and wrangler dev (API) in parallel
  const vite = spawn("npx", ["vite"], {
    cwd: resolvedDir,
    stdio: ["ignore", "pipe", "pipe"],
    env: { ...process.env },
  });

  const wrangler = spawn("npx", [
    "wrangler", "dev",
    `--port=${apiPort}`,
    `--config=${wranglerPath}`,
  ], {
    cwd: resolvedDir,
    stdio: ["ignore", "pipe", "pipe"],
    env: { ...process.env },
  });

  // Filter output — suppress noisy wrangler/vite startup lines, show errors
  const filterVite = createFilter("ui");
  const filterWrangler = createFilter("api");

  vite.stdout?.on("data", filterVite);
  vite.stderr?.on("data", filterVite);
  wrangler.stdout?.on("data", filterWrangler);
  wrangler.stderr?.on("data", filterWrangler);

  // Handle exit
  const cleanup = () => {
    vite.kill();
    wrangler.kill();
  };

  process.on("SIGINT", cleanup);
  process.on("SIGTERM", cleanup);

  // Exit when either process exits
  const exitPromise = Promise.race([
    new Promise<number>((resolve) => vite.on("exit", (code) => resolve(code ?? 1))),
    new Promise<number>((resolve) => wrangler.on("exit", (code) => resolve(code ?? 1))),
  ]);

  const code = await exitPromise;
  cleanup();
  process.exit(code);
}

function createFilter(label: string): (data: Buffer) => void {
  // Suppress wrangler banner, version lines, "Ready on" (we already printed URLs)
  const suppress = [
    /^\s*⛅️/,
    /^\s*wrangler\s/,
    /^Starting local server/,
    /^Ready on http/,
    /^\s*\[mf:inf\]/,
    /^\s*╭/,
    /^\s*│/,
    /^\s*╰/,
    /^\s*$/,
    // Vite startup noise
    /VITE v/,
    /ready in \d+/,
    /Local:\s+http/,
    /Network:/,
    /press h \+ enter/,
  ];

  return (data: Buffer) => {
    const lines = data.toString().split("\n");
    for (const line of lines) {
      if (!line.trim()) continue;
      if (suppress.some((re) => re.test(line))) continue;
      // Show meaningful output with label
      console.log(`  ${label} │ ${line}`);
    }
  };
}

function runQuiet(cwd: string, cmd: string, args: string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const proc = spawn(cmd, args, {
      cwd,
      stdio: ["ignore", "pipe", "pipe"],
      env: { ...process.env },
    });

    let stderr = "";
    proc.stderr?.on("data", (d: Buffer) => { stderr += d.toString(); });

    proc.on("exit", (code) => {
      if (code === 0) resolve();
      else {
        console.error(`  Schema init failed: ${stderr.trim()}`);
        reject(new Error(`Command exited with code ${code}`));
      }
    });
  });
}

/**
 * Ensure local D1 matches the current schema.ts via drizzle-kit.
 *
 * The contract we expose to the user is: edit schema.ts, run
 * `pnpm dev`, it just works. They never touch migration SQL by hand.
 *
 * Flow:
 *   1. `drizzle-kit generate` (writes new migrations under
 *      .clawnify/drizzle/ per drizzle.config.ts). No-op if schema.ts
 *      hasn't changed since the last generate.
 *   2. Read .clawnify/applied-migrations.json (or []) — the list of
 *      .sql files we've already applied to this local D1.
 *   3. For each .sql file in .clawnify/drizzle/ not yet in the list,
 *      apply via `wrangler d1 execute --local`, then record it.
 *   4. Write the updated list back.
 *
 * Per-app local state. Each dev's local D1 has its own applied list;
 * production D1 has its own tracking in __clawnify_migrations
 * (handled server-side by the deploy pipeline).
 */
async function syncDrizzleToLocalD1(
  resolvedDir: string,
  clawnifyDir: string,
  slug: string,
  wranglerPath: string,
): Promise<void> {
  try {
    await runQuiet(resolvedDir, "npx", ["drizzle-kit", "generate"]);
  } catch (err) {
    console.error(
      `  drizzle-kit generate failed. Is \`drizzle-kit\` in devDependencies?\n  ${(err as Error).message}`,
    );
    throw err;
  }

  const drizzleDir = path.join(clawnifyDir, "drizzle");
  if (!fs.existsSync(drizzleDir)) return; // nothing to apply yet

  const sqlFiles = fs
    .readdirSync(drizzleDir)
    .filter((name) => name.endsWith(".sql"))
    .sort();
  if (sqlFiles.length === 0) return;

  const appliedPath = path.join(clawnifyDir, "applied-migrations.json");
  let applied: string[] = [];
  if (fs.existsSync(appliedPath)) {
    try {
      applied = JSON.parse(fs.readFileSync(appliedPath, "utf8")) as string[];
      if (!Array.isArray(applied)) applied = [];
    } catch {
      applied = [];
    }
  }
  const appliedSet = new Set(applied);

  for (const file of sqlFiles) {
    if (appliedSet.has(file)) continue;
    const filePath = path.join(drizzleDir, file);
    await runQuiet(resolvedDir, "npx", [
      "wrangler", "d1", "execute", `${slug}-db`,
      "--local", `--file=${filePath}`,
      `--config=${wranglerPath}`,
    ]);
    applied.push(file);
  }

  fs.writeFileSync(appliedPath, JSON.stringify(applied, null, 2) + "\n");
}
